import {
  Box,
  Grid,
  IconButton,
  Popover,
  styled,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { MouseEvent, useState } from 'react';
import { DynamicIcon, Tooltip } from 'src/components';

interface IconGridSelectorProps {
  icons: string[];
  value?: string;
  onChange: (value: string) => void;
  sx?: SxProps<Theme>;
  emptyLabel?: string;
  placeholderIcon?: string;
  tooltipLabel?: string;
}

const SelectorBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  background: theme.palette.custom.OV.sys.surfaceContainerHighest,
  border: `1px solid ${theme.palette.custom.OV.sys.outline}`,
  color: theme.palette.custom.OV.sys.outline,
  width: '2rem',
  height: '2rem',
  cursor: 'pointer',
  borderRadius: theme.shape.borderRadius,
}));

export const IconGridSelector = (props: IconGridSelectorProps) => {
  const {
    sx = {},
    value,
    icons,
    emptyLabel = 'Select icon',
    placeholderIcon,
    tooltipLabel = 'Select an icon',
    onChange,
  } = props;
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);

  const handleOpen = (event: MouseEvent) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const displayedIconName = value || placeholderIcon;

  return (
    <>
      <SelectorBox sx={sx} onClick={handleOpen}>
        {displayedIconName ? (
          <Tooltip title={tooltipLabel}>
            <DynamicIcon name={displayedIconName} />
          </Tooltip>
        ) : (
          <Typography variant="body2">{emptyLabel}</Typography>
        )}
      </SelectorBox>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box padding={2} maxWidth={300}>
          <Grid container spacing={1} columns={6}>
            {icons.map((icon) => {
              const isSelected = icon === value;
              return (
                <Grid item xs={1} key={icon}>
                  <IconButton
                    onClick={() => {
                      onChange(icon);
                      handleClose();
                    }}
                    sx={
                      isSelected
                        ? (theme) => ({
                            bgcolor: theme.palette.action.selected,
                            color: theme.palette.primary.main,
                            '&:hover': { bgcolor: theme.palette.action.selected },
                          })
                        : undefined
                    }
                  >
                    <DynamicIcon name={icon} />
                  </IconButton>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Popover>
    </>
  );
};
